import {
  CheckboxSelectionCallbackParams,
  RowDataUpdatedEvent,
  RowNode,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Predicates } from "libraries/predicates/predicates";
import { toComponentMaterial } from "helpers/material.helper";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { IMpnComposition, ITccComposition } from "types/data.interface";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { CreateMpnActions } from "../../states/create";
import { CreateMpnActionTypes } from "../../states/create/data";

const GPCValuesImportTable = ({
  isInApprovalProcess,
  isRevision,
  composition,
  isSummaryPage,
  isViewOnly,
}: {
  isInApprovalProcess: boolean;
  isRevision: boolean;
  composition: ITccComposition[];
  isSummaryPage?: boolean;
  isViewOnly?: boolean;
}) => {
  const { state, dispatch } = useCreateMpnStore();

  const gridRef = useRef<AgGridReact<ITccComposition>>(null);

  const setSelectedCompositionRows = (value: number[]) => {

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_SELECTED_ITEM_NUMBER_COMPOSITION_ROWS,
        [],
      ),
    );

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_SELECTED_GP_COMPOSITION_ROWS,
        value,
      ),
    );
  };

  const setUsedMoldIndexes = (value: string[]) => {
    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_SELECTED_MOLD_INDEXES,
        value,
      ),
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        valueGetter: (params: any) => {
          return `${params.data?.gpc_comnum}`;
        },
        headerName: "Component Number",
        maxWidth: 150,
      },
      {
        headerName: "Component Type",
        valueGetter: (params: any) =>
          !!params.data?.gpc_mldcod ? "Molded" : "Non-Molded",
      },

      {
        headerName: "Component",
        valueGetter: (params: any) => {
          return !!params.data?.gpc_mldcod
            ? `${params.data?.gpc_mldcod} - ${params.data?.gpc_mldnam}`
            : `${params.data?.gpc_nmlcod} - ${params.data?.gpc_nmlnam}`;
        },
      },

      {
        headerName: "Marketing Material type",
        valueGetter: (params: any) => params.data?.mktMaterial ?? "---",
      },
      {
        headerName: "Color",
        valueGetter: (params: any) =>
          params.data?.color?.id
            ? `${params.data?.color?.id} - ${params.data?.color?.name}`
            : "---",
      },
      {
        headerName: "Component mandatory (Y/N)",
        valueGetter: (params: any) => params.data?.gpc_commnd ?? "N",
      },
      {
        headerName: "Component pieces",
        valueGetter: (params: any) => {
          return params.data?.gpc_compcs;
        },
      },
      {
        headerName: "Select",
        sortable: false,
        maxWidth: 100,
        checkboxSelection: (
          params: CheckboxSelectionCallbackParams<ITccComposition>,
        ) => checkIfIsSelectable(params?.node),
        showDisabledCheckboxes: isViewOnly,
        hide: isSummaryPage || isViewOnly,
      },
    ],
    [
      isInApprovalProcess,
      isRevision,
      state?.selectedGpCompositionRows,
      state?.selectedMoldIndexes,
      composition,
    ],
  );

  const defaultColDef = useMemo(
    () => ({
      sortingOrder: ["asc" as const, "desc" as const],
      minWidth: 100,
      sortable: true,
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      suppressMenu: true,
      suppressMovable: true,
      icons: {
        sortAscending: "<i class='fa fa-sort-up'/>",
        sortDescending: "<i class='fa fa-sort-down'/>",
        sortUnSort: "<i class='fa fa-sort' style='color:#e3e6f0'></i>",
        filter: "<i class='fa fa-filter'></i>",
      },
    }),
    [],
  );

  const handleOnSelectionChanged = useCallback(() => {
    const selectedRows = Array.from(
      new Set(gridRef?.current?.api.getSelectedRows()),
    );

    const mpnComp: IMpnComposition = {
      ...state.composition,
      molded_components: [],
      non_molded_components: [],
      mpns: [],
      skus: [],
    };

    selectedRows.forEach((comp: any) => {
      const mappedComp: any = {
        artwork_description: "",
        artwork_id: 0,
        color_description: comp?.color ? comp.color.name : "",
        color_id: comp?.color ? comp.color.id : "",
        color_family_name: comp?.color ? comp.color?.color_family_name : "",
        decoration_technique_description: "",
        decoration_technique_id: 0,
        full_objects: {
          artwork: null,
          color: null,
          decoration_technique: null,
          material: null,
          mold: null,
          non_mold: null,
        },
        importedTccCompositionId: 0,
        is_tps: false,
        material_description: comp.nonMold ? toComponentMaterial(comp.nonMold.material).material_description ?? "" : "",
        material_id: comp.nonMold ? toComponentMaterial(comp.nonMold.material).material_id ?? 0 : 0,
        mold_description: !!comp?.gpc_mldcod
          ? `${comp?.gpc_mldnam}`
          : `${comp?.gpc_nmlnam}`,
        mold_id: !!comp?.gpc_mldcod
          ? `${comp?.gpc_mldcod}`
          : `${comp?.gpc_nmlcod}`,
        sequence_number: Number(comp?.gpc_nmlcod),
        mold_index: comp.gpc_comnum,
        node_id: comp.id,
        nr_pieces: Number(comp.gpc_compcs),
        quantity: 1,
        type: comp.gpc_mldcod ? "Molded" : "NonMolded",
        componentTypeOf: comp?.gpc_comtyp,
        isMandatory: comp?.gpc_commnd,
        mktMaterial: comp?.mktMaterial,
      };

      if (mappedComp.type === "Molded") {
        mpnComp.molded_components.push(mappedComp);
        return;
      }
      mpnComp.non_molded_components.push(mappedComp);
    });

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_COMPOSITION,
        mpnComp,
      ),
    );

    setSelectedCompositionRows(
      gridRef?.current?.api.getSelectedRows().map((row) => row.id) ?? [],
    );

    setUsedMoldIndexes(
      Array.from(
        new Set(
          gridRef?.current?.api
            .getSelectedRows()
            .filter((composition) =>
              Predicates.isNotNullAndNotUndefinedAndNotEmpty(composition.index),
            )
            .map((composition) => composition.index ?? ""),
        ),
      ) ?? [],
    );

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_IS_CHANGED_IMPORTED_VALUES,
        true,
      ),
    );
  }, []);

  const checkIfIsSelectable = (rowNode: RowNode) => {
    return (
      state.selectedItemNumberCompositionRows.length < 1 &&
      !isInApprovalProcess &&
      !isRevision &&
      (Predicates.isNullOrUndefinedOrEmpty(rowNode.data?.index) ||
        Predicates.isNotNullAndNotUndefined(
          state.selectedGpCompositionRows.find((id) => id === rowNode.data?.id),
        ) ||
        !state.selectedMoldIndexes.includes(rowNode.data?.index ?? ""))
    );
  };

  const onRowDataUpdated = (params: RowDataUpdatedEvent<ITccComposition>) => {
    params.api.forEachNode((rowNode: RowNode) => {
      rowNode.setSelected(
        Predicates.isNotNullAndNotUndefined(
          state.selectedGpCompositionRows.find(
            (id) => id === rowNode.data?.id,
          ),
        ),
        false,
        true,
      );
    });
  };

  useEffect(() => {
    const updateSelectable = (rowNode: RowNode) => {
      rowNode.setRowSelectable(checkIfIsSelectable(rowNode));
    };

    if (gridRef.current?.api) {
      gridRef?.current?.api.forEachNode(updateSelectable);
    }
  }, [state?.selectedMoldIndexes]);

  return (
    <>
      <div className="ag-theme-alpine">
        <AgGridReact
          domLayout="autoHeight"
          ref={gridRef}
          rowData={composition}
          getRowId={(params) => String(params.data.id)}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          suppressRowClickSelection
          unSortIcon
          suppressPaginationPanel
          rowSelection="multiple"
          onRowDataUpdated={onRowDataUpdated}
          onSelectionChanged={() => handleOnSelectionChanged()}
        ></AgGridReact>
      </div>
    </>
  );
};

export default GPCValuesImportTable;
